import type { ActionLimits, AutomationSettings, BrandVoice, PipelineStage } from "./types";

/** Seeded for every new user (ids are generated at insert time). */
export const DEFAULT_STAGES: Omit<PipelineStage, "id">[] = [
  { name: "New Lead", color: "#8b5cf6", position: 0, kind: "open" },
  { name: "Contacted", color: "#6366f1", position: 1, kind: "open" },
  { name: "Replied", color: "#ec4899", position: 2, kind: "open" },
  { name: "Qualified", color: "#f97316", position: 3, kind: "open" },
  { name: "Won", color: "#22c55e", position: 4, kind: "won" },
  { name: "Lost", color: "#64748b", position: 5, kind: "lost" },
];

/**
 * Conservative per-IG-account caps. Instagram action-blocks accounts that act
 * faster than a human; these defaults keep a warmed-up account well clear of
 * that. Users can raise them in Settings at their own risk.
 */
export const DEFAULT_LIMITS: ActionLimits = {
  follow: { perDay: 60, perHour: 12 },
  unfollow: { perDay: 60, perHour: 12 },
  like: { perDay: 150, perHour: 30 },
  comment: { perDay: 25, perHour: 6 },
  story_view: { perDay: 300, perHour: 60 },
  dm_new: { perDay: 30, perHour: 8 },
  dm_reply: { perDay: 200, perHour: 50 },
  publish: { perDay: 3, perHour: 1 },
  edit_post: { perDay: 30, perHour: 6 },
};

/** Hard ceilings the API refuses to exceed regardless of user settings. */
export const MAX_LIMITS: ActionLimits = {
  follow: { perDay: 200, perHour: 40 },
  unfollow: { perDay: 200, perHour: 40 },
  like: { perDay: 500, perHour: 80 },
  comment: { perDay: 100, perHour: 20 },
  story_view: { perDay: 1000, perHour: 200 },
  dm_new: { perDay: 100, perHour: 20 },
  dm_reply: { perDay: 500, perHour: 100 },
  publish: { perDay: 10, perHour: 3 },
  edit_post: { perDay: 100, perHour: 20 },
};

export const DEFAULT_AUTOMATION: AutomationSettings = {
  enabled: false,
  limits: DEFAULT_LIMITS,
  minDelaySec: 45,
  maxDelaySec: 120,
  activeStart: "08:00",
  activeEnd: "22:00",
  timezone: "America/Chicago",
  allowBackgroundTab: true,
};

export const DEFAULT_VOICE: BrandVoice = {
  businessName: "",
  whatYouSell: "",
  idealCustomer: "",
  tone: "friendly, confident, straight to the point",
  callToAction: "",
  link: "",
  signOff: "",
  examples: [],
  avoid: [],
};

/** Plan → how many IG accounts a user may connect (starter/growth/pro match PLAN_TIER_INFO in ./plans). */
export const PLAN_IG_ACCOUNT_LIMIT: Record<string, number> = {
  trial: 1,
  starter: 1,
  growth: 2,
  pro: 5,
  lifetime: 3,
  agency: 10,
};

/** Client seats in the Reseller Program, and the seat limit that means "unlimited" (fits the admin editor). */
export const RESELLER_PROGRAM_SEATS = 100;
export const UNLIMITED_SEATS = 100000;

export const PRODUCTION_ORIGIN = "https://app.gramfather.com";
export const DEV_API_ORIGIN = "http://localhost:8787";
